import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import CartItems from './CartItems';
import Totals from './Totals';
import KlarnaCheckout from './KlarnaCheckout';
import checkoutAnalytics from './checkoutAnalytics';
import Input from '../Elements/Input/Input';
import Button from '../Elements/Button/Button';
import Select from '../Elements/Select/Select';
import Radio from '../Elements/Radio/Radio';
import Checkbox from '../Elements/Checkbox/Checkbox';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import {
	addVoucher,
	initiatedScript,
	voucherInputChange,
	removeVoucher,
	changeCountry,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	checkAddressInputErrors,
	booleanChange,
	updateKlarna,
	checkProceed,
	purchase,
} from '../../actions/checkout';
import { translate, camelCase } from '../../utils/string';
import { purifier } from '../../utils/dom';
import { paymentMethodsMapping, shippingMethods, shippingMethodsOrder } from '../../config';
import s from './Selection.scss';

class Selection extends Component {
	componentDidMount() {
		const { checkout, setup } = this.props;
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		if (checkout.selection.items && checkout.selection.items.length > 0) {
			checkoutAnalytics(checkout.selection.items, setup, checkout.selection.totals, checkout.listItemCache);
		}
	}

	render() {
		const { checkout, setup } = this.props;
		const { selection, shippingAvailable, deliveryProceed } = checkout;
		const { totals, paymentFields } = selection;
		const currentCurrency = setup.currencies[setup.countries[setup.country].currency];
		const freightLimit = parseInt(setup.statics[`freeFreightFrom${currentCurrency.name}`], 10);
		const amountUntilFreeFreight = totals && totals.shippingPriceAsNumber > 0 ? freightLimit - totals.itemsTotalPriceAsNumber : 0;
		const freightReplacementText = `${currentCurrency.prefix}${amountUntilFreeFreight.toFixed(2)}${currentCurrency.suffix}`;

		let hasShipping = false;
		if (paymentFields?.shippingAddress) {
			Object.keys(paymentFields.shippingAddress).forEach((field) => {
				if (paymentFields.shippingAddress[field].visible) hasShipping = true;
			});
		}
		const fieldOrder = ['firstName', 'lastName', 'company', 'phoneNumber', 'address1', 'address2', 'zipCode', 'city', 'state'];
		const hasAddressFields = paymentFields?.address ? !!fieldOrder.filter((field) => field !== 'state' && paymentFields.address[field].visible).length : false;

		const addVoucherHandler = (e) => {
			e.preventDefault();
			this.props.addVoucher().then(() => this.props.updateKlarna()).catch((err) => console.log('DEBUG: voucher not added'));
		};
		const removeVoucherHandler = (e) => {
			e.preventDefault();
			const voucherId = e.target.dataset.voucher;
			this.props.removeVoucher(voucherId).then(() => this.props.updateKlarna()).catch((err) => console.log('DEBUG: voucher not removed'));
		};
		const onVoucherInputChange = (id, value) => {
			this.props.voucherInputChange(value);
		};

		const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: setup.countries[country].name, disabled: !setup.countries[country].shipTo }] : [...acc]), []);
		const onCountryChange = (id, value) => {
			this.props.changeAddressInput('address', 'country', value);
			this.props.changeAddressInput('shippingAddress', 'country', value);
			this.props.changeCountry(value);
		};
		const onShippingCountryChange = (id, value) => {
			this.props.changeAddressInput('shippingAddress', 'country', value);
			this.props.changeCountry(value);
		};

		const availablePaymentMethods = selection.paymentMethods ? (
			selection.paymentMethods.reduce((acc, method) => {
				if (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents) return acc;
				return [
					...acc,
					{
						key: method.paymentMethod,
						value: setup.statics[`${camelCase(method.paymentMethod)}Payment`] ? translate(setup, `${camelCase(method.paymentMethod)}Payment`) : method.name,
					},
				];
			}, [])) : [];
		const hasKlarna = selection.paymentMethods ? selection.paymentMethods.filter((method) => method.paymentMethod === paymentMethodsMapping.klarnaCheckout) : false;
		const onPaymentMethodChange = (value) => {
			this.props.changePaymentMethod(value);
		};

		const availableShippingMethods = selection.shippingMethods ? (
			selection.shippingMethods.reduce((acc, method) => [
				...acc,
				{
					key: method.shippingMethod,
					value: method.name.replace(/^\d+/, ''),
					disabled: shippingAvailable[method.shippingMethod] === false,
					icon: method.shippingMethod,
					info: translate(setup, `${camelCase(method.shippingMethod)}Shipping`).replace('%shipping%', totals.shippingPrice),
				},
			], [])).sort((a, b) => (shippingMethodsOrder.indexOf(a.key) < shippingMethodsOrder.indexOf(b.key) ? -1 : 1)) : [];
		const hasAirmee = selection.shippingMethods ? selection.shippingMethods.reduce((acc, method) => (method.shippingMethod === shippingMethods.airmee ? { ...acc, [method.shippingMethod]: method } : acc), {}) : false;
		const onShippingMethodChange = (value) => {
			this.props.changeShippingMethod(value);
		};

		const onAddressInputChange = (id, value) => {
			const group = id.slice(0, id.indexOf('-'));
			const fieldId = id.slice(id.indexOf('-') + 1);
			this.props.changeAddressInput(group, fieldId, value);
			this.props.checkAddressInputErrors(group, fieldId, value);
		};

		const onAddressInputBlur = (id, value) => {
			onAddressInputChange(id, value);
		};

		const checkProceedHandler = (e) => {
			e.preventDefault();
			this.props.checkProceed();
		};

		const onBooleanChange = (id, value) => {
			this.props.booleanChange(id);
			if (selection.paymentMethod === paymentMethodsMapping.klarnaCheckout && id === 'subscribeToNewsletter') this.props.updateKlarna();
			if (id === 'useShipping' && value === false) {
				this.props.changeAddressInput('address', 'country', checkout.payment.shippingAddress.country);
			}
			this.props.checkAddressInputErrors('', id, value);
		};

		const onPurchaseHandler = () => {
			this.props.purchase();
		};

		return (
			<main role="main">
				{
					amountUntilFreeFreight > 0 ? (
						<div className={s.freeFreight}>
							<span>{ translate(setup, 'toFreeFreight').replace('###', freightReplacementText) }</span>
						</div>
					) : null
				}
				{
					!selection.items || selection.items.length === 0 ? (
						<div className={s.selection}>
							{ checkout.selectionErrorStock ? <div className={s.selection_side_purchase_error}>{ translate(setup, 'checkoutStockErrorNoProducts') }</div> : null }
							<h1 className={s.selection_side_title}>{ translate(setup, 'checkoutNoItems') }</h1>
						</div>
					) : (
						<div className={s.selection}>
							<div className={s.selection_side}>
								<ErrorBoundary message="Product data missing" debugData={selection}>
									<h1 className={s.selection_side_title}>{ translate(setup, 'checkoutYourItemsTitle') }</h1>
									<CartItems currentPage="selection" />
									<div className={s.selection_side_totals}>
										<div className={s.selection_side_totals_voucher}>
											<form onSubmit={addVoucherHandler}>
												{
													selection.discounts && selection.discounts.vouchers.length > 0 ? (
														<div className={s.selection_side_totals_voucher_added}>
															{
																selection.discounts.vouchers.map((voucher) => (
																	<a key={voucher.voucher} className={s.selection_side_totals_voucher_added_row} href="#removeVoucher" data-voucher={voucher.voucher} onClick={removeVoucherHandler}><span>{voucher.voucher}</span></a>
																))
															}
														</div>
													) : null
												}
												<Input id="voucherCode" type="text" placeholder={translate(setup, 'checkoutVoucherCode')} value={checkout.vouchers.currentValue} label={translate(setup, 'checkoutHaveAVoucher')} inputChange={onVoucherInputChange} />
												<Button type="submit" text={translate(setup, 'checkoutVoucherUse')} />
												{ checkout.vouchers.error ? <div className={s.selection_side_totals_voucher_error}>{ translate(setup, 'checkoutVoucherError') }</div> : null }
											</form>
										</div>
										<Totals setup={setup} selection={selection} />
									</div>
									{ setup.statics.checkoutCustomerService ? <div className={s.selection_side_customerService} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutCustomerService')) }} /> : null }
								</ErrorBoundary>
							</div>
							{
								setup.unableToSendToCountry ? (
									<div className={s.selection_side}>
										<h2 className={s.selection_side_title}>{ translate(setup, 'checkoutDeliveryTitleChangeCountry') }</h2>
										<div className={s.selection_side_country}>
											<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} />
										</div>
									</div>
								) : (
									<div className={s.selection_side}>
										<h2 className={s.selection_side_title}>{ translate(setup, 'checkoutDetailsTitle') }</h2>
										<form className={s.selection_side_proceed} onSubmit={checkProceedHandler}>
											<div className={[s.selection_side_country, (availableShippingMethods.length > 0 ? s.selection_side_country__shippingMethods : null)].join(' ')}>
												<span className={s.selection_side_country_title}>{ `${translate(setup, 'checkoutShippingTo')}: ${setup.countries[setup.country].name}` }</span>
												<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} />
											</div>
											<div className={s.selection_side_address_row}>
												<Input id="address-email" type="email" value={checkout.payment.address.email} placeholder={translate(setup, 'emailLabel')} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={selection?.paymentFields?.address?.email?.error} />
											</div>
											{
												Object.keys(hasAirmee).length > 0 && (
													<div className={[s.selection_side_zipCode, s.selection_side_zipCode__proceed].join(' ')}>
														<Input id="address-zipCode" type="text" value={checkout.payment.address.zipCode} placeholder={translate(setup, 'zipCodeLabel')} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={selection?.paymentFields?.address?.zipCode?.error} />
													</div>
												)
											}
											<Checkbox id="subscribeToNewsletter" label={translate(setup, 'subscribeToNewsletter')} selected={checkout.payment.address.newsletter} inputChange={onBooleanChange} />
											<Checkbox id="termsAndConditions" innerHTML={translate(setup, 'checkoutAcceptTerms')} selected={checkout.payment.termsAndConditions} inputChange={onBooleanChange} isError={selection?.paymentFields?.termsAndConditions?.error} />
											{ !deliveryProceed && <Button type="submit" text={translate(setup, 'zipCodeProceed')} /> }
										</form>
										{
											!deliveryProceed ? (
												<>
													<h2 className={[s.selection_side_title, s.selection_side_title__disabled].join(' ')}>{ translate(setup, 'checkoutDeliveryTitle') }</h2>
													<h2 className={[s.selection_side_title, s.selection_side_title__disabled].join(' ')}>{ translate(setup, 'checkoutPaymentTitle') }</h2>
												</>
											) : (
												<>
													<h2 className={s.selection_side_title}>{ translate(setup, 'checkoutDeliveryTitle') }</h2>
													<div className={s.selection_side_country}>
														<ErrorBoundary message="Shipping methods" debugData={selection}>
															<div className={s.selection_side_payment_choice}>
																<Radio id="shipping" selected={selection.shippingMethod} options={availableShippingMethods} inputChange={onShippingMethodChange} />
															</div>
														</ErrorBoundary>
													</div>
													<h2 className={s.selection_side_title}>{ translate(setup, 'checkoutPaymentTitle') }</h2>
													<div className={s.selection_side_payment}>
														<ErrorBoundary message="Payment methods" debugData={selection}>
															<div className={s.selection_side_payment_choice}>
																<Radio id="payment" selected={selection.paymentMethod} options={availablePaymentMethods} inputChange={onPaymentMethodChange} />
															</div>
														</ErrorBoundary>
													</div>
													<div className={s.selection_side_address}>
														{ hasAddressFields ? <span className={s.selection_side_address_information}>{ translate(setup, 'latinCharactersOnly') }</span> : null }
														<ErrorBoundary message="Billing address" debugData={paymentFields}>
															{
																fieldOrder.map((field) => {
																	if (!paymentFields.address || !paymentFields.address[field].visible) return null;
																	if (field === 'state') {
																		if (setup.countries[setup.country].states) {
																			const states = Object.keys(setup.countries[setup.country].states).map((state) => ({ key: state, value: setup.countries[setup.country].states[state] }));
																			return (
																				<div key={`address-${field}`} className={s.selection_side_address_row}>
																					<Select id={`address-${field}`} selected={checkout.payment.address[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `${field}Label`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={selection.paymentFields?.address[field].error} />
																				</div>
																			);
																		}
																		return null;
																	}
																	const readOnlyField = field === 'zipCode' && Object.keys(hasAirmee).length > 0 && ((hasShipping && !checkout.useShippingAddress) || !hasShipping) ? 1 : 0;
																	const fieldEvents = readOnlyField ? {} : {
																		inputChange: onAddressInputChange,
																		inputBlur: onAddressInputBlur,
																		isError: selection.paymentFields.address[field].error,
																	};
																	return (
																		<div key={`address-${field}`} className={[s.selection_side_address_row, (readOnlyField ? s.selection_side_address_row__disabled : null)].join(' ')}>
																			<Input id={`address-${field}`} type={field === 'email' ? 'email' : 'text'} readOnly={readOnlyField} value={checkout.payment.address[field]} label={translate(setup, `${field}Label`)} {...fieldEvents} />
																		</div>
																	);
																})
															}
															{
																hasShipping ? (
																	<div className={s.selection_side_address_row}>
																		<Select id="address-country" selected={checkout.payment.address.country} options={countries} label={translate(setup, 'countryLabel')} inputChange={onAddressInputChange} />
																	</div>
																) : null
															}
														</ErrorBoundary>
													</div>
													{
														hasShipping ? (
															<div className={s.selection_side_shippingAddress}>
																<ErrorBoundary message="Shipping address" debugData={paymentFields}>
																	<Checkbox id="useShipping" label={translate(setup, 'checkoutAlternateAddress')} selected={checkout.useShippingAddress} inputChange={onBooleanChange} />
																	{
																		checkout.useShippingAddress ? fieldOrder.map((field) => {
																			if (!paymentFields.address || !paymentFields?.address[field].visible) return null;
																			if (field === 'state') {
																				if (setup.countries[setup.country].states) {
																					const states = Object.keys(setup.countries[setup.country].states).map((state) => ({ key: state, value: setup.countries[setup.country].states[state] }));
																					return (
																						<div key={`shippingAddress-${field}`} className={s.selection_side_address_row}>
																							<Select id={`shippingAddress-${field}`} selected={checkout.payment.shippingAddress[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `${field}Label`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={selection.paymentFields?.shippingAddress[field].error} />
																						</div>
																					);
																				}
																				return null;
																			}
																			return (
																				<div key={`shippingAddress-${field}`} className={s.selection_side_address_row}>
																					<Input id={`shippingAddress-${field}`} type={field === 'email' ? 'email' : 'text'} value={checkout.payment.shippingAddress[field]} label={translate(setup, `${field}Label`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={selection.paymentFields?.shippingAddress[field].error} />
																				</div>
																			);
																		}) : null
																	}
																	{
																		checkout.useShippingAddress ? (
																			<div className={s.selection_side_address_row}>
																				<Select id="shippingAddress-country" selected={checkout.payment.shippingAddress.country} options={countries} label={translate(setup, 'countryLabel')} inputChange={onShippingCountryChange} />
																			</div>
																		) : null
																	}
																</ErrorBoundary>
															</div>
														) : null
													}
													{
														hasKlarna.length > 0 ? (
															<div className={s.selection_side_purchase}>
																<ErrorBoundary message="Klarna checkout" debugData={selection}>
																	<KlarnaCheckout paymentMethod={selection.paymentMethod} />
																</ErrorBoundary>
															</div>
														) : null
													}
													{
														paymentFields?.termsAndConditions?.visible ? (
															<div className={s.selection_side_purchase}>
																<ErrorBoundary message="Complete payment" debugData={checkout.payment}>
																	<span className={s.selection_side_purchase_totalHeader}>{ translate(setup, 'checkoutTotalOrder') }</span>
																	<h2 className={s.selection_side_purchase_total}>{ totals.grandTotalPrice }</h2>
																	{ selection.paymentMethod.indexOf('paypal') !== -1 ? <div className={s.selection_side_purchase_error}>{ translate(setup, 'paypalMessage') }</div> : null }
																	{ checkout.selectionError && <div className={s.selection_side_purchase_error}>{ translate(setup, 'checkoutGeneralError') }</div> }
																	{ checkout.selectionErrorStock && <div className={s.selection_side_purchase_error}>{ translate(setup, 'checkoutStockError') }</div> }
																	<Button text={translate(setup, 'checkoutPayment')} type="submit" clickEventHandler={onPurchaseHandler} />
																</ErrorBoundary>
															</div>
														) : null
													}
												</>
											)
										}
									</div>
								)
							}
						</div>
					)
				}
			</main>
		);
	}
}

const mapState = ({ checkout, environment }) => (
	{ checkout, setup: environment.setup }
);

export default hot(module)(connect(mapState, {
	addVoucher,
	initiatedScript,
	voucherInputChange,
	removeVoucher,
	changeCountry,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	checkAddressInputErrors,
	booleanChange,
	updateKlarna,
	checkProceed,
	purchase,
})(Selection));
